const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/components/old_components/components/Label.tsx';
import * as React from 'react';
import { ReactNode } from 'react';
import { MessageDescriptor } from 'react-intl';
import { css, cx } from '@emotion/css';
import { Icon, IconButton, Theme } from '@mui/material';
import { HelpDialog } from '@cistec/common_web/components/Forms/components/HelpDialog/HelpDialog';
import { useTheme } from '@mui/material/styles';
import useBreakpoint from '@cistec/common_web/hooks/useBreakpoint';
import { StaticText } from './StaticText';

export enum AriaLabelType {
    useText,
    useNone,
}

export type IconLabelType = 'help' | 'info' | undefined;

export interface LabelProps {
    text: string | MessageDescriptor;
    helpText?: string | MessageDescriptor | ReactNode;
    textWidth?: number;
    marginTopInFormEdit?: number;
    contentClassName?: string;
    contentStyle?: React.CSSProperties;
    contentFullWidth?: boolean;
    children?: ReactNode;
    // WAI ARIA for/id construct
    aria: string | AriaLabelType | undefined;
    isFormView?: boolean;
    input?: any;
    error?: any;
    isMandatory?: boolean;
    icon?: IconLabelType;
    iconOverrideStyle?: string;
    compTitle?: string | MessageDescriptor;
    onClick?: (e: any) => void;
    className?: string;
}

const textStyle = (theme: Theme, isSmallDevice: boolean) => css`
    flex: 0 0 ${isSmallDevice ? 6 : 8}rem;
    position: relative;
    margin-right: ${theme.spacing(1)};
    max-width: ${isSmallDevice ? 55 : 8}rem;
`;

const iconStyle = (theme: Theme) => css`
    position: absolute;
    top: ${theme.spacing(-1.5)};
`;

const helpIconStyle = (theme: Theme) => css`
    position: absolute;
    top: ${theme.spacing(-1.5)};
    right: ${theme.spacing(-1)};
`;

const infoIconStyle = (theme: Theme) => css`
    position: absolute;
    top: ${theme.spacing(-1.5)};
    right: -210px;
`;

// Removed ctx-based field with commit 7da5929cc
export const Label = (props: LabelProps) => {
    const theme = useTheme();
    const isSmallDevice = useBreakpoint(theme?.breakpoints?.values?.sm);

    const [canSeeHelpText, setCanSeeHelpText] = React.useState(false);

    const helpBtnClicked = () => setCanSeeHelpText(true);

    const hasTextDiv = !!props.text || !props.isFormView;
    const hasHelpIcon = !!props.helpText && !props.isFormView;

    const styleIconForHelpText = () => {
        if (props.iconOverrideStyle) {
            return props.iconOverrideStyle;
        }
        switch (props.icon) {
            case 'help':
                return helpIconStyle(theme);
            case 'info':
                return infoIconStyle(theme);
            default:
                return iconStyle(theme);
        }
    };

    const onCloseHelp = () => setCanSeeHelpText(false);

    const ariaId = typeof props.aria === 'string' ? props.aria : undefined;

    const textInlineStyle: React.CSSProperties = {
        flex: props.textWidth === undefined ? undefined : `0 0 ${props.textWidth}px`,
        marginTop: !props.isFormView ? (props.marginTopInFormEdit ?? theme.spacing(0.75)) : undefined,
    };

    return (
        <>
            {!hasTextDiv ? null : (
                <div
                    className={cx(textStyle(theme, isSmallDevice), props.className)}
                    style={textInlineStyle}
                    onClick={props.onClick}
                >
                    <StaticText
                        isFormView={props.isFormView}
                        component="div"
                        textComponent="label"
                        text={props.text}
                        forId={ariaId}
                        color={'textSecondary'}
                        isMandatory={props.isMandatory}
                        formatMessageParams={{ br: <br /> }}
                    />
                    {hasHelpIcon && (
                        <>
                            <IconButton onClick={helpBtnClicked} size="large" className={styleIconForHelpText()}>
                                <Icon className={`icons8-${props.icon}`} />
                            </IconButton>

                            <HelpDialog
                                title={props.text || props.compTitle || ''}
                                text={props.helpText}
                                isOpen={canSeeHelpText}
                                onClose={onCloseHelp}
                            />
                        </>
                    )}
                </div>
            )}
        </>
    );
};
